import React, { useEffect, useState, useRef } from 'react';
import { UserIcon, ShieldIcon, BellIcon, CameraIcon, SaveIcon, EyeIcon, EyeOffIcon, CheckIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, LockIcon, KeyIcon, AlertTriangleIcon, GlobeIcon, Loader2Icon, SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { getGoogleApiKey, setGoogleApiKey, validateGoogleApiKey, hasGoogleApiKey } from '../lib/googleMaps';
type Tab = 'perfil' | 'seguridad' | 'notificaciones' | 'integraciones';
const AVATAR_KEY = 'ventastrack_avatar';
const NOTIFS_KEY = 'ventastrack_notifs';
const defaultNotifs = {
  visitasCompletadas: true,
  nuevosCobros: true,
  nuevasVentas: false,
  reportesDiarios: true,
  alertasEquipo: false,
  recordatoriosVisita: true
};
export type NotifPrefs = typeof defaultNotifs;
export function loadNotifPrefs(): NotifPrefs {
  try {
    const saved = localStorage.getItem(NOTIFS_KEY);
    if (saved) return {
      ...defaultNotifs,
      ...JSON.parse(saved)
    };
  } catch {}
  return defaultNotifs;
}
export function loadAvatarUrl(userId?: string): string {
  if (userId) {
    const userSpecific = localStorage.getItem(`ventastrack_avatar_${userId}`);
    if (userSpecific) return userSpecific;
  }
  // Don't fall back to global key — each user has their own avatar
  return '';
}
export function SettingsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for settings sub-tabs
  const canSeePerfil = isAdmin || user?.permisos?.settings_perfil === true || user?.permisos?.settings_perfil === undefined;
  const canSeeSeguridad = isAdmin || user?.permisos?.settings_seguridad === true || user?.permisos?.settings_seguridad === undefined;
  const canSeeNotificaciones = isAdmin || user?.permisos?.settings_notificaciones === true || user?.permisos?.settings_notificaciones === undefined;
  const canSeeIntegraciones = isAdmin || user?.permisos?.settings_integraciones === true;
  const [activeTab, setActiveTab] = useState<Tab>('perfil');
  // Perfil
  const [avatarUrl, setAvatarUrl] = useState<string>(() => loadAvatarUrl(user?.id));
  const [nombre, setNombre] = useState(user?.nombre || '');
  // Cargar avatar desde Supabase al iniciar (para sincronizar entre navegadores)
  useEffect(() => {
    if (!user?.id) return;
    const loadFromSupabase = async () => {
      const {
        data
      } = await supabaseAdmin.from('profiles').select('avatar_url').eq('id', user.id).single();
      if (data?.avatar_url) {
        setAvatarUrl(data.avatar_url);
        localStorage.setItem(`ventastrack_avatar_${user.id}`, data.avatar_url);
        window.dispatchEvent(new Event('avatar-updated'));
      }
    };
    loadFromSupabase();
  }, [user?.id]);
  const [email, setEmail] = useState(user?.email || '');
  const [perfilSaved, setPerfilSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seguridad
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Notificaciones
  const [notifs, setNotifs] = useState<NotifPrefs>(loadNotifPrefs);
  const [notifSaved, setNotifSaved] = useState(false);
  // Integraciones
  const [googleKey, setGoogleKey] = useState(getGoogleApiKey);
  const [googleKeyInput, setGoogleKeyInput] = useState(getGoogleApiKey);
  const [isValidatingKey, setIsValidatingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [integrationSaved, setIntegrationSaved] = useState(false);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };
  const strengthColors = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500'];
  const strengthLabels = ['Muy débil', 'Débil', 'Buena', 'Fuerte'];
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const base64 = ev.target?.result as string;
      setAvatarUrl(base64);
      if (user?.id) {
        localStorage.setItem(`ventastrack_avatar_${user.id}`, base64);
        // Guardar en Supabase para sincronizar entre navegadores
        await supabaseAdmin.from('profiles').update({
          avatar_url: base64
        }).eq('id', user.id);
      }
      window.dispatchEvent(new Event('avatar-updated'));
    };
    reader.readAsDataURL(file);
  };
  const handleSavePerfil = () => {
    setPerfilSaved(true);
    setTimeout(() => setPerfilSaved(false), 2500);
  };
  const handleSavePassword = () => {
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Ingresa tu contraseña actual');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    addActivityLog('Cambió contraseña', 'Actualización de contraseña');
    setPasswordSaved(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPasswordSaved(false), 2500);
  };
  const handleToggleNotif = (key: keyof NotifPrefs) => {
    setNotifs(prev => {
      const updated = {
        ...prev,
        [key]: !prev[key]
      };
      localStorage.setItem(NOTIFS_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifs-updated'));
      return updated;
    });
  };
  const handleSaveNotifs = () => {
    localStorage.setItem(NOTIFS_KEY, JSON.stringify(notifs));
    window.dispatchEvent(new Event('notifs-updated'));
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };
  const handleValidateGoogleKey = async () => {
    if (!googleKeyInput.trim()) return;
    setIsValidatingKey(true);
    setKeyValidation('idle');
    try {
      const isValid = await validateGoogleApiKey(googleKeyInput.trim());
      setKeyValidation(isValid ? 'valid' : 'invalid');
    } catch {
      setKeyValidation('invalid');
    } finally {
      setIsValidatingKey(false);
    }
  };
  const handleSaveGoogleKey = () => {
    setGoogleApiKey(googleKeyInput.trim());
    setGoogleKey(googleKeyInput.trim());
    addActivityLog('Guardó API Key Google Maps', 'Integración Google Maps configurada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const handleRemoveGoogleKey = () => {
    setGoogleApiKey('');
    setGoogleKey('');
    setGoogleKeyInput('');
    setKeyValidation('idle');
    addActivityLog('Eliminó API Key Google Maps', 'Integración Google Maps desconectada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const notifItems: {
    key: keyof NotifPrefs;
    label: string;
    desc: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    key: 'visitasCompletadas',
    label: 'Visitas completadas',
    desc: 'Cuando un vendedor completa una visita',
    icon: <MapPinIcon className="w-4 h-4" data-id="element-3753" />,
    color: 'bg-[#eef2fc] text-[#2952C4]'
  }, {
    key: 'nuevosCobros',
    label: 'Nuevos cobros',
    desc: 'Alertas de cobros registrados en el sistema',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-3754" />,
    color: 'bg-emerald-100 text-emerald-600'
  }, {
    key: 'nuevasVentas',
    label: 'Nuevas ventas',
    desc: 'Alertas de ventas registradas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-3755" />,
    color: 'bg-blue-100 text-blue-600'
  }, {
    key: 'reportesDiarios',
    label: 'Reportes diarios',
    desc: 'Resumen diario de actividad del equipo',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-3756" />,
    color: 'bg-[#d5e0f7] text-[#1E3D96]'
  }, {
    key: 'alertasEquipo',
    label: 'Alertas del equipo',
    desc: 'Actividad y cambios del equipo comercial',
    icon: <UsersIcon className="w-4 h-4" data-id="element-3757" />,
    color: 'bg-amber-100 text-amber-600'
  }, {
    key: 'recordatoriosVisita',
    label: 'Recordatorios de visita',
    desc: 'Recordatorios de próximas visitas programadas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3758" />,
    color: 'bg-rose-100 text-rose-600'
  }];
  const allTabs: {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
  }[] = [{
    id: 'perfil',
    label: 'Perfil',
    icon: <UserIcon className="w-5 h-5" data-id="element-3759" />,
    visible: canSeePerfil
  }, {
    id: 'seguridad',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-5 h-5" data-id="element-3760" />,
    visible: canSeeSeguridad
  }, {
    id: 'notificaciones',
    label: 'Notificaciones',
    icon: <BellIcon className="w-5 h-5" data-id="element-3761" />,
    visible: canSeeNotificaciones
  }, {
    id: 'integraciones',
    label: 'Integraciones',
    icon: <GlobeIcon className="w-5 h-5" data-id="element-3762" />,
    visible: canSeeIntegraciones
  }];
  const tabs = allTabs.filter(t => t.visible);
  // Auto-select first visible tab if current is hidden
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  const activeNotifCount = Object.values(notifs).filter(Boolean).length;
  const pwdStrength = getPasswordStrength(newPassword);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3763">
      <PageHeader title="Configuración" subtitle="Ajustes de cuenta y preferencias del sistema" icon={<SettingsIcon className="w-6 h-6" data-id="element-3765" />} data-id="element-3764" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-stagger-1" data-id="element-3766">
        {/* Sidebar */}
        <div className="md:col-span-1 space-y-4" data-id="element-3767">
          {/* User card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center" data-id="element-3768">
            <div className="relative inline-block mb-4" data-id="element-3769">
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg mx-auto" style={{
              background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
            }} data-id="element-3770">
                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-3771" /> : <span className="text-2xl font-bold text-white" data-id="element-3772">
                    {getInitials(nombre || user?.nombre || '??')}
                  </span>}
              </div>
              <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg transition-colors" style={{
              backgroundColor: '#2952C4'
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-3773">
                <CameraIcon className="w-3.5 h-3.5 text-white" data-id="element-3774" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-id="element-3775" />
            </div>
            <p className="font-bold text-slate-900 font-heading" data-id="element-3776">
              {user?.nombre}
            </p>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-3777">{user?.rolNombre}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200" data-id="element-3778">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3779" />
              Activo
            </span>
          </div>

          {/* Tab navigation */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3780">
            {tabs.map((tab, idx) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-5 py-4 text-sm font-medium transition-all ${idx < tabs.length - 1 ? 'border-b border-slate-100' : ''}`} style={activeTab === tab.id ? {
            backgroundColor: '#eef2fc',
            color: '#1E3D96',
            borderRight: '2px solid #2952C4'
          } : {}} onMouseEnter={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f8fafc';
          }} onMouseLeave={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '';
          }} data-id="element-3781">
                <span style={activeTab === tab.id ? {
              color: '#2952C4'
            } : {
              color: '#94a3b8'
            }} data-id="element-3782">
                  {tab.icon}
                </span>
                <span className={activeTab === tab.id ? 'font-heading' : 'text-slate-600 font-heading'} data-id="element-3783">
                  {tab.label}
                </span>
                {tab.id === 'notificaciones' && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{
              backgroundColor: '#eef2fc',
              color: '#2952C4'
            }} data-id="element-3784">
                    {activeNotifCount}
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Right content */}
        <div className="md:col-span-3" data-id="element-3785">
          {/* PERFIL TAB */}
          {activeTab === 'perfil' && canSeePerfil && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3786">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-3787">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3788">
                  Información del Perfil
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-3789">
                  Actualiza tu información personal y foto de perfil
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-3790">
                <div className="flex items-center gap-6 p-5 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-id="element-3791">
                  <div className="relative flex-shrink-0" data-id="element-3792">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-md" style={{
                  background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
                }} data-id="element-3793">
                      {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-3794" /> : <span className="text-2xl font-bold text-white" data-id="element-3795">
                          {getInitials(nombre || user?.nombre || '??')}
                        </span>}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-3796">
                    <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-3797">
                      Foto de perfil
                    </p>
                    <p className="text-xs text-slate-500 mb-3" data-id="element-3798">
                      JPG, PNG o GIF. Máximo 5MB. Se guarda automáticamente.
                    </p>
                    <div className="flex items-center gap-2" data-id="element-3799">
                      <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm" style={{
                    backgroundColor: '#2952C4'
                  }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-3800">
                        <CameraIcon className="w-4 h-4" data-id="element-3801" />
                        Subir foto
                      </button>
                      {avatarUrl && <button onClick={async () => {
                    setAvatarUrl('');
                    if (user?.id) {
                      localStorage.removeItem(`ventastrack_avatar_${user.id}`);
                      await supabaseAdmin.from('profiles').update({
                        avatar_url: null
                      }).eq('id', user.id);
                    }
                    window.dispatchEvent(new Event('avatar-updated'));
                  }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-3802">
                          Eliminar
                        </button>}
                    </div>
                  </div>
                </div>

                {isAdmin ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3803">
                    <Input label="Nombre Completo" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre completo" data-id="element-3804" />
                    <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" data-id="element-3805" />
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3806">
                    <div data-id="element-3807">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3808">
                        Nombre Completo
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3809">
                        <UserIcon className="w-4 h-4 text-slate-400" data-id="element-3810" />
                        <span className="text-sm text-slate-600" data-id="element-3811">
                          {user?.nombre}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-3812">
                          Solo admin
                        </span>
                      </div>
                    </div>
                    <div data-id="element-3813">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3814">
                        Correo Electrónico
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3815">
                        <span className="text-sm text-slate-600" data-id="element-3816">
                          {user?.email || '—'}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-3817">
                          Solo admin
                        </span>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-3818">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3819">
                    Rol del Sistema
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3820">
                    <ShieldIcon className="w-4 h-4 text-slate-400" data-id="element-3821" />
                    <span className="text-sm text-slate-600" data-id="element-3822">
                      {user?.rolNombre}
                    </span>
                    <span className="ml-auto text-xs text-slate-400" data-id="element-3823">
                      No editable
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-3824">
                {perfilSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3825">
                    <CheckIcon className="w-4 h-4" data-id="element-3826" />
                    Cambios guardados
                  </div>}
                <Button onClick={handleSavePerfil} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3828" />} data-id="element-3827">
                  Guardar Cambios
                </Button>
              </div>
            </div>}

          {/* SEGURIDAD TAB */}
          {activeTab === 'seguridad' && canSeeSeguridad && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3829">
              <div className="px-8 py-6 border-b border-slate-100 bg-slate-50" data-id="element-3830">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3831">
                  Seguridad de la Cuenta
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-3832">
                  Actualiza tu contraseña para mantener tu cuenta segura
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-3833">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-3834">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3835" />
                  <div data-id="element-3836">
                    <p className="text-sm font-semibold text-amber-800" data-id="element-3837">
                      Recomendación de seguridad
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-3838">
                      Usa una contraseña de al menos 10 caracteres con letras,
                      números y símbolos.
                    </p>
                  </div>
                </div>

                <div className="space-y-5" data-id="element-3839">
                  <div data-id="element-3840">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3841">
                      Contraseña Actual
                    </label>
                    <div className="relative" data-id="element-3842">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3843">
                        <KeyIcon className="w-4 h-4" data-id="element-3844" />
                      </div>
                      <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" style={{
                    '--tw-ring-color': 'rgba(41,82,196,0.2)',
                    '--tw-border-opacity': '1'
                  } as React.CSSProperties} data-id="element-3845" />
                      <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3846">
                        {showCurrent ? <EyeOffIcon className="w-4 h-4" data-id="element-3847" /> : <EyeIcon className="w-4 h-4" data-id="element-3848" />}
                      </button>
                    </div>
                  </div>

                  <div data-id="element-3849">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3850">
                      Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-3851">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3852">
                        <LockIcon className="w-4 h-4" data-id="element-3853" />
                      </div>
                      <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" data-id="element-3854" />
                      <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3855">
                        {showNew ? <EyeOffIcon className="w-4 h-4" data-id="element-3856" /> : <EyeIcon className="w-4 h-4" data-id="element-3857" />}
                      </button>
                    </div>
                    {newPassword && <div className="mt-3" data-id="element-3858">
                        <div className="flex gap-1.5 mb-1.5" data-id="element-3859">
                          {[0, 1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i < pwdStrength ? strengthColors[pwdStrength - 1] : 'bg-slate-200'}`} data-id="element-3860" />)}
                        </div>
                        <p className={`text-xs font-medium ${pwdStrength <= 1 ? 'text-rose-500' : pwdStrength === 2 ? 'text-amber-500' : pwdStrength === 3 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-3861">
                          {strengthLabels[pwdStrength - 1] || 'Muy débil'}
                        </p>
                      </div>}
                  </div>

                  <div data-id="element-3862">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3863">
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-3864">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3865">
                        <LockIcon className="w-4 h-4" data-id="element-3866" />
                      </div>
                      <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" className={`w-full pl-10 pr-12 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white ${confirmPassword && newPassword ? confirmPassword === newPassword ? 'border-emerald-400' : 'border-rose-400' : 'border-slate-200'}`} data-id="element-3867" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3868">
                        {showConfirm ? <EyeOffIcon className="w-4 h-4" data-id="element-3869" /> : <EyeIcon className="w-4 h-4" data-id="element-3870" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && <p className={`mt-1.5 text-xs font-medium flex items-center gap-1 ${confirmPassword === newPassword ? 'text-emerald-600' : 'text-rose-500'}`} data-id="element-3871">
                        {confirmPassword === newPassword ? <>
                            <CheckIcon className="w-3 h-3" data-id="element-3872" />
                            Las contraseñas coinciden
                          </> : '✗ Las contraseñas no coinciden'}
                      </p>}
                  </div>
                </div>

                {passwordError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3873">
                    <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-3874" />
                    {passwordError}
                  </div>}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-3875">
                {passwordSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3876">
                    <CheckIcon className="w-4 h-4" data-id="element-3877" />
                    Contraseña actualizada
                  </div>}
                <Button onClick={handleSavePassword} leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-3879" />} data-id="element-3878">
                  Actualizar Contraseña
                </Button>
              </div>
            </div>}

          {/* NOTIFICACIONES TAB */}
          {activeTab === 'notificaciones' && canSeeNotificaciones && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3880">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-3881">
                <div className="flex items-center justify-between" data-id="element-3882">
                  <div data-id="element-3883">
                    <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3884">
                      Notificaciones
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-3885">
                      Controla qué alertas deseas recibir
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-3886">
                    <BellIcon className="w-4 h-4" style={{
                  color: '#2952C4'
                }} data-id="element-3887" />
                    <span className="text-sm font-bold text-slate-900" data-id="element-3888">
                      {activeNotifCount}
                    </span>
                    <span className="text-xs text-slate-500" data-id="element-3889">activas</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-3" data-id="element-3890">
                {notifItems.map(item => <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border transition-all" style={notifs[item.key] ? {
              borderColor: '#aac1ef',
              backgroundColor: 'rgba(238,242,252,0.5)'
            } : {
              borderColor: '#f1f5f9',
              backgroundColor: 'white'
            }} data-id="element-3891">
                    <div className="flex items-center gap-4" data-id="element-3892">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`} data-id="element-3893">
                        {item.icon}
                      </div>
                      <div data-id="element-3894">
                        <p className="text-sm font-semibold text-slate-900" data-id="element-3895">
                          {item.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-3896">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0" data-id="element-3897">
                      <input type="checkbox" className="sr-only peer" checked={notifs[item.key]} onChange={() => handleToggleNotif(item.key)} data-id="element-3898" />
                      <div className="brand-toggle w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" data-id="element-3899" />
                    </label>
                  </div>)}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-3900">
                <p className="text-sm text-slate-500" data-id="element-3901">
                  <span className="font-semibold text-slate-700" data-id="element-3902">
                    {activeNotifCount}
                  </span>{' '}
                  de {notifItems.length} notificaciones activas
                </p>
                <div className="flex items-center gap-3" data-id="element-3903">
                  {notifSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3904">
                      <CheckIcon className="w-4 h-4" data-id="element-3905" />
                      Guardado
                    </div>}
                  <Button onClick={handleSaveNotifs} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3907" />} data-id="element-3906">
                    Guardar Preferencias
                  </Button>
                </div>
              </div>
            </div>}

          {/* INTEGRACIONES TAB */}
          {activeTab === 'integraciones' && canSeeIntegraciones && <div className="space-y-6" data-id="element-3908">
              {/* Google Maps Card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3909">
                <div className="px-8 py-6 border-b border-slate-100" style={{
              background: 'linear-gradient(90deg, #e8f5e9, #f1f8e9)'
            }} data-id="element-3910">
                  <div className="flex items-center gap-4" data-id="element-3911">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center" data-id="element-3912">
                      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" data-id="element-3913">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-3914" />
                        <circle cx="12" cy="9" r="3" fill="white" data-id="element-3915" />
                      </svg>
                    </div>
                    <div data-id="element-3916">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-heading" data-id="element-3917">
                        Google Maps Platform
                        {googleKey && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full" data-id="element-3918">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3919" />
                            Conectado
                          </span>}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-3920">
                        Mapas precisos, autocomplete de direcciones y GPS en
                        República Dominicana
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6" data-id="element-3921">
                  {/* Benefits */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-3922">
                    {[{
                  icon: '🗺️',
                  label: 'Mapas actualizados RD',
                  desc: 'Los más precisos disponibles'
                }, {
                  icon: '🔍',
                  label: 'Autocomplete',
                  desc: 'Búsqueda moderna de direcciones'
                }, {
                  icon: '📍',
                  label: 'GPS preciso',
                  desc: 'Ubicación exacta de farmacias'
                }, {
                  icon: '🧭',
                  label: 'Detección de llegada',
                  desc: 'Verificar visitas automáticamente'
                }].map(b => <div key={b.label} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-3923">
                        <span className="text-lg" data-id="element-3924">{b.icon}</span>
                        <div data-id="element-3925">
                          <p className="text-sm font-semibold text-slate-900" data-id="element-3926">
                            {b.label}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-3927">{b.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  {/* API Key Input */}
                  <div data-id="element-3928">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3929">
                      API Key de Google Maps
                    </label>
                    <div className="flex gap-2" data-id="element-3930">
                      <div className="relative flex-1" data-id="element-3931">
                        <KeyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3932" />
                        <input type="password" value={googleKeyInput} onChange={e => {
                      setGoogleKeyInput(e.target.value);
                      setKeyValidation('idle');
                    }} placeholder="AIzaSy..." className={`w-full pl-10 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white font-mono ${keyValidation === 'valid' ? 'border-emerald-400 ring-emerald-200' : keyValidation === 'invalid' ? 'border-rose-400 ring-rose-200' : 'border-slate-200'}`} data-id="element-3933" />
                      </div>
                      <Button variant="secondary" onClick={handleValidateGoogleKey} disabled={!googleKeyInput.trim() || isValidatingKey} data-id="element-3934">
                        {isValidatingKey ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-3935" /> : 'Verificar'}
                      </Button>
                    </div>
                    {keyValidation === 'valid' && <p className="mt-2 text-sm text-emerald-600 font-medium flex items-center gap-1" data-id="element-3936">
                        <CheckIcon className="w-4 h-4" data-id="element-3937" /> API Key válida —
                        Google Maps está listo
                      </p>}
                    {keyValidation === 'invalid' && <p className="mt-2 text-sm text-rose-600 font-medium flex items-center gap-1" data-id="element-3938">
                        <AlertTriangleIcon className="w-4 h-4" data-id="element-3939" /> API Key
                        inválida — Verifica que esté correcta y tenga los APIs
                        habilitados
                      </p>}
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-id="element-3940">
                    <p className="text-sm font-semibold text-blue-800 mb-2" data-id="element-3941">
                      ¿Cómo obtener tu API Key?
                    </p>
                    <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside" data-id="element-3942">
                      <li data-id="element-3943">
                        Ve a{' '}
                        <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-900" data-id="element-3944">
                          Google Cloud Console
                        </a>
                      </li>
                      <li data-id="element-3945">Crea un proyecto nuevo o selecciona uno existente</li>
                      <li data-id="element-3946">
                        Habilita estos APIs:{' '}
                        <strong data-id="element-3947">Maps JavaScript API</strong>,{' '}
                        <strong data-id="element-3948">Places API</strong>,{' '}
                        <strong data-id="element-3949">Geocoding API</strong>
                      </li>
                      <li data-id="element-3950">
                        Ve a "Credenciales" → "Crear credenciales" → "API Key"
                      </li>
                      <li data-id="element-3951">Copia la API Key y pégala aquí</li>
                    </ol>
                  </div>
                </div>

                <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-3952">
                  <div data-id="element-3953">
                    {googleKey && <button onClick={handleRemoveGoogleKey} className="text-sm text-rose-600 hover:text-rose-700 font-medium hover:underline" data-id="element-3954">
                        Desconectar Google Maps
                      </button>}
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3955">
                    {integrationSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3956">
                        <CheckIcon className="w-4 h-4" data-id="element-3957" />
                        Guardado
                      </div>}
                    <Button onClick={handleSaveGoogleKey} disabled={!googleKeyInput.trim()} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3959" />} data-id="element-3958">
                      Guardar API Key
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-id="element-3960">
                <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-3961">
                  Estado de Integraciones
                </h4>
                <div className="space-y-3" data-id="element-3962">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-3963">
                    <div className="flex items-center gap-3" data-id="element-3964">
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center" data-id="element-3965">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" data-id="element-3966">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-3967" />
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-3968" />
                        </svg>
                      </div>
                      <div data-id="element-3969">
                        <p className="text-sm font-medium text-slate-900" data-id="element-3970">
                          Google Maps
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3971">
                          Mapas, Places, Geocoding
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${googleKey ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-3972">
                      <span className={`w-1.5 h-1.5 rounded-full ${googleKey ? 'bg-emerald-500' : 'bg-slate-400'}`} data-id="element-3973" />
                      {googleKey ? 'Activo' : 'No configurado'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-3974">
                    <div className="flex items-center gap-3" data-id="element-3975">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-3976">
                        <MapPinIcon className="w-4 h-4 text-blue-600" data-id="element-3977" />
                      </div>
                      <div data-id="element-3978">
                        <p className="text-sm font-medium text-slate-900" data-id="element-3979">
                          Photon + OSM
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3980">
                          Búsqueda gratuita (fallback)
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-3981">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3982" />
                      Siempre activo
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}